import React, { useState } from 'react';
import Seo from '@/components/Seo';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Logo } from '@/components/Logo';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import PasswordInput from '@/components/PasswordInput';
import { toast } from 'sonner';
import api from '@/lib/api';
import { KeyRound, ArrowRight } from 'lucide-react';

export default function ResetPassword() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get('token') || '';
    const [pw1, setPw1] = useState('');
    const [pw2, setPw2] = useState('');
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (pw1.length < 10) { toast.error('Password must be at least 10 characters'); return; }
        if (!/[A-Za-z]/.test(pw1) || !/\d/.test(pw1)) {
            toast.error('Password must include at least one letter and one number.');
            return;
        }
        if (pw1 !== pw2) { toast.error('Passwords do not match'); return; }
        setLoading(true);
        try {
            const r = await api.post('/auth/reset-password', { token, new_password: pw1 });
            toast.success('Password updated! You can now log in.');
            navigate(`/login?email=${encodeURIComponent(r.data.email || '')}`, { replace: true });
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Could not reset password');
        } finally { setLoading(false); }
    };

    return (
        <div className="min-h-dvh bg-[#f4f7fb] flex flex-col">
            <Seo
                title="Choose a new password | SplashPad"
                description="Set a new password for your SplashPad account."
                canonical="https://splashpad.pro/reset-password"
                noIndex
            />
            <div className="px-4 py-4 sm:px-6"><Link to="/"><Logo /></Link></div>
            <div className="flex flex-1 items-center justify-center px-4 pb-32 sm:pb-12 sm:px-6">
                <Card className="sd-card w-full max-w-md p-6 sm:p-8">
                    <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#00d4ff]/15">
                        <KeyRound className="h-5 w-5 text-[#0a1628]" />
                    </div>
                    <h1 className="font-display text-2xl font-bold text-[#0a1628]">Set a new password</h1>
                    <p className="mt-1 text-sm text-[#1a3a5c]/80">Pick something at least 10 characters long, with a letter and a number. You\u2019ll be logged in to use it after.</p>

                    {!token ? (
                        <div className="mt-5 rounded-xl bg-[#ff6b35]/10 p-4 text-sm text-[#ff6b35]">
                            This reset link is missing a token. Request a new one from the
                            {' '}<Link to="/forgot-password" className="font-semibold underline">forgot password page</Link>.
                        </div>
                    ) : (
                        <form onSubmit={submit} className="mt-5 space-y-4" data-testid="reset-form">
                            <div>
                                <Label>New password</Label>
                                <PasswordInput required value={pw1} onChange={(e) => setPw1(e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="reset-pw1-input" testIdShow="reset-pw1-toggle" />
                            </div>
                            <div>
                                <Label>Confirm new password</Label>
                                <PasswordInput required value={pw2} onChange={(e) => setPw2(e.target.value)} className="mt-1 h-11 rounded-xl" data-testid="reset-pw2-input" testIdShow="reset-pw2-toggle" />
                            </div>
                            <Button type="submit" disabled={loading} className="sd-cta sd-focus w-full h-11 rounded-xl" data-testid="reset-submit-button">
                                {loading ? 'Updating…' : 'Update password'} <ArrowRight className="ml-1 h-4 w-4" />
                            </Button>
                        </form>
                    )}

                    <div className="mt-6 text-sm text-[#1a3a5c]">
                        <Link to="/login" className="font-semibold text-[#0a1628] hover:underline">Back to log in</Link>
                    </div>
                </Card>
            </div>
        </div>
    );
}
